<script setup>
/**
 * READ IT BACK — the map of what the owner just wrote, and the one button that puts it in
 * front of customers.
 *
 * ── THE MAP IS A RECEIPT, NEVER A DRAWING BOARD ───────────────────────────────────────
 * `TaskMap.vue` and its 682-line pure layout are mounted exactly as they are, read-only,
 * against the graph the list compiles to. That is the whole argument of the redesign in one
 * screen: the diagram is something you are SHOWN at the end, not something you draw. Every
 * box is a way back to the step it draws, through the `data-waslo-task-step` hook the step
 * screen carries — the one markup contract the two halves share.
 *
 * ── AND WHY THE MAP IS ALSO THE ANSWER TO A LIST THAT DOES NOT READ STRAIGHT DOWN ─────
 * `spineJumps` — not `unreadable` — is the real signal. Chunk 0 proved the list expresses
 * essentially every valid graph, so `unreadable` almost never fires; what genuinely varies
 * is whether the list reads top to bottom. When it does not, the map is where the shape is
 * legible, and this screen says so rather than hiding it behind a tab.
 *
 * ── THE BUTTON NAMES ITS OUTCOME ──────────────────────────────────────────────────────
 * "Turn this task on", not "Save". On the create path this is the first and only write to
 * the server: nothing exists until it is pressed, so leaving for the connection editor and
 * coming back loses nothing.
 *
 * ── AND THIS IS THE SCREEN THAT OWNS THE SWITCH ───────────────────────────────────────
 * A task written here is born SWITCHED OFF (`blankDraft`), so the five-screen path and the
 * state of the thing finally agree: it is not answering anybody until this screen says so,
 * after the rehearsal above it. That is what "Check it" was always for.
 *
 * The label is read off what the SERVER has (`live`), never off the draft, and it used to
 * be read off the draft — with the arms crossed. `enabled ? 'TURN_ON' : 'SAVE_OFF'` put
 * "Turn this task on" on a task that was already on, and put "Save it, switched off" on a
 * task that was off — on a button that then called `save(true)` and switched it ON. A
 * control whose label said the opposite of what it did, on the one screen in the product
 * where that decision is made. Both halves are fixed here: the state is reported, and each
 * button does the thing it is named after.
 *
 * ── AND IT IS WHERE A VERSION IS TRIED FIRST (P-2.11) ─────────────────────────────────
 * "Try it before customers do" opens the rehearsal drawer on this exact draft, saved or not,
 * on or off (the page owns the drawer). The box above the switch says whether THIS version was
 * tried: "Tried 12 minutes ago", or "Not tried since your last change" the moment any step
 * changes, because the record is keyed by the compiled graph. A read that failed says nothing.
 */
import { computed } from 'vue';

import Button from 'dashboard/components-next/button/Button.vue';

import TaskMap from '../TaskMap.vue';
import { useWasloI18n } from '../../i18n.js';
import { toIntlLocale } from '../../helpers/intl.js';
import { relativeTime } from '../../helpers/relative-time.js';
import {
  problemMessage,
  warningMessage,
} from '../../helpers/task-flow-draft.js';

const props = defineProps({
  /** The compiled graph — what the server will be sent. */
  definition: { type: Object, default: null },
  /** The task's own two columns, read back. */
  title: { type: String, default: '' },
  trigger: { type: String, default: '' },
  /** `spineJumps(spine)` — "this list does not read straight down". */
  jumps: { type: Array, default: () => [] },
  /**
   * WHAT THE SERVER HAS, not what the draft intends. A task is live when the stored row
   * says so; an unsaved intention to switch it on is not a customer reading anything.
   * Defaults to `false`, which is also what a task that does not exist yet is.
   */
  live: { type: Boolean, default: false },
  /** True when there is something to write — a new task, or an edited one. */
  pending: { type: Boolean, default: false },
  /** True while a write is in flight. */
  isSaving: { type: Boolean, default: false },
  /** Anything still wrong. Non-empty means the button is not offered. */
  problems: { type: Array, default: () => [] },
  /** `[{stepId, question}]`: each change step and the question whose yes lets it run (P-3.4). */
  changeNotes: { type: Array, default: () => [] },
  /**
   * Worth a look, never a block: `[{stepId, problem, detail}]` (P-2.2, P-10.10, P-10.21,
   * P-10.23), from `graphWarnings`. Each says what is worth a look in the owner's own names,
   * with "Show me" and, where there is one, a one-click fix (`fix`).
   */
  warnings: { type: Array, default: () => [] },
  /** P-10.9: how it starts, `{ mode, words }` (`startOf`). */
  start: { type: Object, default: () => ({ mode: 'assistant', words: [] }) },
  /** P-10.9: a menu-started task that no menu starts, so no customer can reach it. */
  noMenu: { type: Boolean, default: false },
  /**
   * P-2.11: `{ rehearsedAt, turns }` for THIS version, or `null` while unknown (not read yet,
   * or the read failed), which draws no line at all.
   */
  rehearsal: { type: Object, default: null },
});

const emit = defineEmits([
  'turn-on',
  'turn-off',
  'save',
  'select',
  'fix',
  'try',
]);

/** The one-click fix a warning offers, by kind, or none. */
const FIX_OF = {
  failure_path_on_normal_path: 'SKIP_WHEN_WORKS',
  kept_for_chat_only: 'SAVE_TO_PROFILE',
  connection_off: 'OPEN_CONNECTION',
};

const { t, locale } = useWasloI18n();

const key = name => `WASLO.TASKS.COMPOSE.CHECK.${name}`;

const format = computed(
  () => new Intl.NumberFormat(toIntlLocale(locale.value))
);

const stepCount = computed(() =>
  format.value.format((props.definition?.steps ?? []).length)
);

/** P-10.9: the start line says how THIS task starts. */
const startsLine = computed(() => {
  if (props.start?.mode === 'words') {
    return t(key('STARTS_WORDS'), {
      words: (props.start.words ?? []).join(
        locale.value === 'ar' ? '، ' : ', '
      ),
    });
  }
  if (props.start?.mode === 'menu') return t(key('STARTS_MENU'));
  return t(key('STARTS'), { trigger: props.trigger });
});

/** P-2.11: "Tried 12 minutes ago" / "Not tried since your last change" / nothing. */
const triedLine = computed(() => {
  if (!props.rehearsal) return '';
  const when = relativeTime(props.rehearsal.rehearsedAt, locale.value, {
    justNow: t('WASLO.TASKS.LIST.UPDATED_JUST_NOW'),
  });
  return when ? t(key('TRIED'), { when }) : t(key('NOT_TRIED'));
});

const CONNECTION_KINDS = ['unconfirmed_change', 'live_needs_lookup'];
const needsConfirm = computed(() =>
  props.problems.filter(problem => CONNECTION_KINDS.includes(problem.problem))
);
</script>

<template>
  <section class="flex flex-col gap-6" data-waslo-check-screen>
    <div class="flex flex-col gap-1">
      <h2 class="mb-0 text-base font-medium text-n-slate-12">
        {{ t(key('HEADING')) }}
      </h2>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key('DESCRIPTION')) }}
      </p>
    </div>

    <div class="flex flex-col gap-2" data-waslo-check-summary>
      <p class="mb-0 text-sm text-n-slate-12">
        <bdi>{{ title || t(key('UNTITLED')) }}</bdi>
      </p>
      <p class="mb-0 text-sm text-n-slate-11" data-waslo-check-starts>
        <bdi>{{ startsLine }}</bdi>
      </p>
      <p class="mb-0 text-sm text-n-slate-11">
        {{ t(key('STEPS'), { count: stepCount }) }}
      </p>
    </div>

    <ul
      v-if="changeNotes.length"
      class="flex flex-col gap-1 mb-0"
      data-waslo-check-changes
    >
      <li
        v-for="note in changeNotes"
        :key="note.stepId"
        class="text-sm text-n-slate-12"
      >
        <bdi>{{ t(key('CHANGES_AFTER'), { question: note.question }) }}</bdi>
      </li>
    </ul>

    <div
      v-if="needsConfirm.length"
      class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1"
      data-waslo-check-needs-confirm
    >
      <p class="mb-0 text-sm font-medium text-n-amber-11">
        {{ t(key('NEEDS_CONFIRM')) }}
      </p>
      <button
        v-for="problem in needsConfirm"
        :key="`${problem.stepId}-${problem.problem}`"
        type="button"
        class="text-sm underline text-start text-n-slate-12"
        @click="emit('select', problem.stepId)"
      >
        {{ problemMessage(problem, t) }}
      </button>
    </div>

    <div
      v-if="warnings.length"
      class="flex flex-col gap-2 p-4 rounded-xl bg-n-alpha-1"
      data-waslo-check-warnings
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key('WARNINGS')) }}
      </p>
      <div
        v-for="warning in warnings"
        :key="`${warning.stepId}-${warning.problem}-${warning.detail}`"
        class="flex flex-col gap-1"
        :data-waslo-check-warning="warning.problem"
      >
        <p class="mb-0 text-sm text-n-slate-11">
          <bdi>{{ warningMessage(warning, definition, t) }}</bdi>
        </p>
        <div class="flex flex-wrap gap-3">
          <button
            type="button"
            class="text-sm underline text-start text-n-slate-12"
            data-waslo-check-show
            @click="emit('select', warning.stepId)"
          >
            {{ t(key('SHOW_ME')) }}
          </button>
          <button
            v-if="FIX_OF[warning.problem]"
            type="button"
            class="text-sm underline text-start text-n-blue-11"
            :data-waslo-check-fix="warning.problem"
            @click="emit('fix', warning)"
          >
            {{ t(key(`FIX.${FIX_OF[warning.problem]}`)) }}
          </button>
        </div>
      </div>
    </div>

    <p
      v-if="noMenu"
      class="p-4 mb-0 text-sm rounded-xl bg-n-alpha-1 text-n-amber-11"
      data-waslo-check-no-menu
    >
      {{ t(key('NO_MENU')) }}
    </p>

    <!-- P-7.1: the form's button and the missing-language warning, supplied by the page. -->
    <slot name="words" />

    <p
      v-if="jumps.length"
      class="mb-0 text-sm text-n-slate-11"
      data-waslo-check-jumps
    >
      {{
        jumps.length === 1
          ? t(key('JUMPS_ONE'))
          : t(key('JUMPS'), { count: format.format(jumps.length) })
      }}
    </p>

    <TaskMap :definition="definition" @select="id => emit('select', id)" />
    <p class="mb-0 text-sm text-n-slate-11">{{ t(key('MAP_NOTE')) }}</p>

    <!-- Where it stands, said before the control that changes it. -->
    <div
      class="flex flex-col gap-1 p-4 rounded-xl bg-n-alpha-1"
      data-waslo-check-live
      :data-live="live ? 'on' : 'off'"
    >
      <p class="mb-0 text-sm font-medium text-n-slate-12">
        {{ t(key(live ? 'LIVE_ON' : 'LIVE_OFF')) }}
      </p>
      <p class="mb-0 text-sm leading-6 max-w-[62ch] text-n-slate-11">
        {{ t(key(live ? 'LIVE_ON_BODY' : 'LIVE_OFF_BODY')) }}
      </p>
      <!-- P-2.11: whether THIS version was tried, and the way to try it. -->
      <div class="flex flex-wrap items-center gap-3 pt-2">
        <Button
          :label="t(key('TRY'))"
          variant="outline"
          color="slate"
          size="sm"
          icon="i-lucide-message-circle-more"
          :disabled="!title.trim() || !definition"
          data-waslo-check-try
          @click="emit('try')"
        />
        <span
          v-if="triedLine"
          class="text-sm text-n-slate-11"
          :data-waslo-check-tried="rehearsal?.rehearsedAt ? 'yes' : 'no'"
        >
          {{ triedLine }}
        </span>
        <span
          v-if="!title.trim()"
          class="text-sm text-n-slate-11"
          data-waslo-check-try-needs-name
        >
          {{ t(key('TRY_NEEDS_NAME')) }}
        </span>
      </div>
    </div>

    <div class="flex flex-wrap items-center gap-3">
      <!--
        Off: turning it on IS the commit, so it is the primary and it also saves whatever
        is pending. Saving it and leaving it off is the quiet second door, offered only
        when there is something to save.
      -->
      <template v-if="!live">
        <Button
          :label="t(key('TURN_ON'))"
          variant="solid"
          color="blue"
          :is-loading="isSaving"
          :disabled="isSaving || problems.length > 0"
          data-waslo-check-turn-on
          @click="emit('turn-on')"
        />
        <Button
          v-if="pending"
          :label="t(key('SAVE_OFF'))"
          variant="faded"
          color="slate"
          :disabled="isSaving || problems.length > 0"
          data-waslo-check-save-off
          @click="emit('save')"
        />
      </template>
      <!--
        On: it is already serving customers, so the primary is the ordinary save and the
        switch reads the other way. Turning it off is never the accent.
      -->
      <template v-else>
        <Button
          v-if="pending"
          :label="t(key('SAVE_CHANGES'))"
          variant="solid"
          color="blue"
          :is-loading="isSaving"
          :disabled="isSaving || problems.length > 0"
          data-waslo-check-save
          @click="emit('save')"
        />
        <Button
          :label="t(key('TURN_OFF'))"
          variant="faded"
          color="slate"
          :disabled="isSaving"
          data-waslo-check-turn-off
          @click="emit('turn-off')"
        />
      </template>
      <p
        v-if="problems.length"
        class="mb-0 text-sm text-n-amber-11"
        data-waslo-check-blocked
      >
        {{ t(key('BLOCKED')) }}
      </p>
    </div>
  </section>
</template>
